import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { CHAT_BLUE } from './constants';
import { API_BASE } from './lobbyReducer';
import { formatChatTime, getInitials, avatarBg } from './helpers';

const CHAT_BG   = '#0f1117';
const CHAT_HEAD = '#1a1d27';

// ── Shared message list renderer ────────────────────────────────────────────
function MessageList({ messages, loading, channelId, isAtBottom, scrollToBottom, messagesRef, onScroll }) {
  return (
    <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
      <Box
        ref={messagesRef}
        onScroll={onScroll}
        sx={{
          position: 'absolute', inset: 0, overflowY: 'auto',
          px: 1, pb: 1, pt: 2,
          display: 'flex', flexDirection: 'column', gap: '4px',
          '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)', borderRadius: 2 },
        }}
      >
        <Box sx={{ flex: 1 }} />

        {!channelId ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center' }}>No channel connected</Typography>
          </Box>
        ) : loading && messages.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={18} sx={{ color: CHAT_BLUE }} />
          </Box>
        ) : messages.filter(m => !m.isDeleted).length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center' }}>No messages yet</Typography>
          </Box>
        ) : messages.filter(m => !m.isDeleted).map((msg, i) => {
          const isOwn = !!msg.isOwn;
          return (
            <Box key={msg._id} sx={{ display: 'flex', gap: 0.75, mt: i === 0 ? 0 : 0.75 }}>
              <Box sx={{ flexShrink: 0, mt: '2px' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '50%', background: avatarBg(msg.authorName), display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff' }}>
                  {getInitials(msg.authorName)}
                </Box>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6, mb: 0.2 }}>
                  <Box sx={{ fontWeight: 700, color: isOwn ? CHAT_BLUE : 'rgba(255,255,255,0.65)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {isOwn ? 'You' : msg.authorName}
                  </Box>
                  <Box sx={{ fontSize: '0.52rem', color: 'rgba(255,255,255,0.22)', flexShrink: 0 }}>
                    {formatChatTime(msg.createdAt)}
                  </Box>
                </Box>
                <Box sx={{ color: '#fff', lineHeight: 1.45, wordBreak: 'break-word' }}>
                  {msg.message}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Scroll-to-bottom button */}
      {!isAtBottom && (
        <Box
          onClick={scrollToBottom}
          sx={{
            position: 'absolute', bottom: 10, right: 10, zIndex: 10,
            width: 28, height: 28, borderRadius: '50%',
            background: CHAT_BG, border: '1px solid rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', pointerEvents: 'all',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
            transition: 'background 0.15s',
            '&:hover': { background: CHAT_BLUE, borderColor: CHAT_BLUE },
          }}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="#fff">
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
          </svg>
        </Box>
      )}
    </Box>
  );
}

// ── Shared compose bar ───────────────────────────────────────────────────────
function ComposeBar({ channelId, composing, setComposing, onSend, inputRef }) {
  if (!channelId) return null;
  return (
    <Box sx={{
      flexShrink: 0, background: CHAT_HEAD,
      borderTop: '1px solid rgba(255,255,255,0.06)',
      px: 0.75, py: 0.75,
      display: 'flex', alignItems: 'flex-end', gap: 0.5,
    }}>
      <Box sx={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: '14px', px: 1.1, py: 0.55, border: '1px solid rgba(255,255,255,0.07)' }}>
        <textarea
          ref={inputRef}
          value={composing}
          onChange={e => setComposing(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          placeholder="Say something…"
          rows={1}
          style={{
            width: '100%', border: 'none', outline: 'none',
            background: 'transparent', resize: 'none',
            fontSize: '0.72rem', fontFamily: 'inherit',
            color: 'rgba(255,255,255,0.88)', lineHeight: 1.4,
            maxHeight: 56, overflowY: 'auto',
          }}
        />
      </Box>
      <Box
        onClick={onSend}
        sx={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          background: composing.trim() ? CHAT_BLUE : 'rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: composing.trim() ? 'pointer' : 'default',
          transition: 'background 0.15s',
        }}
      >
        <Box sx={{ fontSize: 11, color: composing.trim() ? '#fff' : 'rgba(255,255,255,0.25)', transform: 'rotate(-45deg)', mt: '-1px' }}>➤</Box>
      </Box>
    </Box>
  );
}

// ── Hook: shared message loading + sending logic ─────────────────────────────
function useChatLogic({ channelId, participantId, participantName }) {
  const authorName = participantName || 'Guest';
  const [messages,  setMessages]  = useState([]);
  const [composing, setComposing] = useState('');
  const [sending,   setSending]   = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const messagesRef = useRef(null);
  const inputRef    = useRef(null);
  const pollRef     = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!channelId) return;
    try {
      const params = participantId
        ? `?limit=150&participantId=${encodeURIComponent(participantId)}`
        : '?limit=150';
      const res  = await fetch(`${API_BASE}/chat-channels/${channelId}/messages${params}`);
      const json = await res.json();
      if (json.success) setMessages(json.data || []);
    } catch {}
  }, [channelId, participantId]);

  useEffect(() => {
    if (!channelId) return;
    setLoading(true);
    loadMessages().finally(() => setLoading(false));
    pollRef.current = setInterval(loadMessages, 4000);
    return () => clearInterval(pollRef.current);
  }, [channelId, loadMessages]);

  const scrollToBottom = useCallback(() => {
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, []);

  const handleScroll = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return;
    setIsAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
  }, []);

  useEffect(() => {
    if (isAtBottom && messagesRef.current) {
      setTimeout(() => { if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight; }, 50);
    }
  }, [messages.length, isAtBottom]);

  const handleSend = useCallback(async () => {
    const text = composing.trim();
    if (!text || !channelId || sending) return;
    setComposing('');
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/chat-channels/${channelId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, authorName, authorType: 'participant' }),
      });
      const json = await res.json();
      if (json.success) setMessages(prev => [...prev, { ...json.data, isOwn: true }]);
    } catch {}
    finally { setSending(false); }
  }, [composing, channelId, authorName, sending]);

  return { messages, composing, setComposing, loading, isAtBottom, messagesRef, inputRef, scrollToBottom, handleScroll, handleSend };
}

// ── Desktop / non-fullscreen chat (original behaviour) ──────────────────────
function DesktopLivestreamChat({ hotspot, participantName, participantId, headerHeight = 0 }) {
  const action    = hotspot.hotspotAction || {};
  const channelId = action.channelId || null;
  const side      = hotspot.meta?.side || 'right';
  const w         = hotspot.meta?.width ?? 22;

  const [open, setOpen] = useState(true);

  const chat = useChatLogic({ channelId, participantId, participantName });

  useEffect(() => {
    if (open) setTimeout(() => chat.inputRef.current?.focus(), 300);
  }, [open]); // eslint-disable-line

  const panelTranslate = open ? 'translateX(0)' : side === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
  const tabSide = side === 'right' ? 'left' : 'right';

  return (
    <Box sx={{ position: 'absolute', top: headerHeight, [side]: 0, width: `${w}%`, height: `calc(100% - ${headerHeight}px)`, zIndex: 5 + (hotspot.order ?? 0), pointerEvents: 'none' }}>
      {/* Sliding panel */}
      <Box sx={{ position: 'absolute', top: 0, [side]: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'linear-gradient(to top, #0f1117 0%, #0f1117 25%, transparent 100%)', borderLeft: side === 'right' ? '1px solid rgba(255,255,255,0.07)' : 'none', borderRight: side === 'left' ? '1px solid rgba(255,255,255,0.07)' : 'none', transform: panelTranslate, transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)', pointerEvents: 'all' }}>
        <MessageList
          messages={chat.messages}
          loading={chat.loading}
          channelId={channelId}
          isAtBottom={chat.isAtBottom}
          scrollToBottom={chat.scrollToBottom}
          messagesRef={chat.messagesRef}
          onScroll={chat.handleScroll}
        />
        <ComposeBar
          channelId={channelId}
          composing={chat.composing}
          setComposing={chat.setComposing}
          onSend={chat.handleSend}
          inputRef={chat.inputRef}
        />
      </Box>

      {/* Collapse / expand tab */}
      <Box
        onClick={() => setOpen(v => !v)}
        sx={{
          position: 'absolute', top: '50%', [tabSide]: '100%',
          transform: 'translateY(-50%)', width: 20, height: 56,
          background: CHAT_HEAD, borderRadius: side === 'right' ? '6px 0 0 6px' : '0 6px 6px 0',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRight: side === 'right' ? 'none' : undefined,
          borderLeft:  side === 'left'  ? 'none' : undefined,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', pointerEvents: 'all',
          color: 'rgba(255,255,255,0.5)', fontSize: 11,
          transition: 'background 0.15s, color 0.15s',
          '&:hover': { background: CHAT_BLUE, color: '#fff' },
        }}
      >
        <Box sx={{ transform: open ? (side === 'right' ? 'rotate(0)' : 'rotate(180deg)') : (side === 'right' ? 'rotate(180deg)' : 'rotate(0)'), transition: 'transform 0.3s', lineHeight: 1 }}>›</Box>
      </Box>
    </Box>
  );
}

// ── Mobile fullscreen chat ───────────────────────────────────────────────────
// Sits in position:absolute below the video (top = streamPxH).
// When the keyboard opens, visualViewport shrinks — we track that and
// translate the compose bar up so it stays pinned above the keyboard.
// The video above stays fixed because it is a separate DOM element.
function MobileFullscreenChat({ hotspot, participantName, participantId, streamPxH }) {
  const action    = hotspot.hotspotAction || {};
  const channelId = action.channelId || null;

  const chat = useChatLogic({ channelId, participantId, participantName });

  // ── Keyboard / visualViewport awareness ─────────────────────────────────
  // `keyboardOffsetPx` = how many px the visible area has shrunk from the
  // original viewport height.  We translate the compose bar up by that
  // amount so it stays pinned just above the software keyboard.
  const [keyboardOffsetPx, setKeyboardOffsetPx] = useState(0);
  const [vvHeight, setVvHeight] = useState(() => window.visualViewport?.height ?? window.innerHeight);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const fullH  = window.innerHeight;
      const visH   = vv.height;
      const offset = Math.max(0, fullH - visH - (vv.offsetTop || 0));
      setKeyboardOffsetPx(offset);
      setVvHeight(visH);
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Height of the chat area = viewport height minus the video height.
  // When the keyboard is open the visible viewport shrinks; the chat area
  // shrinks too, but the compose bar stays at the bottom via flex layout.
  const chatAreaH = Math.max(60, vvHeight - streamPxH);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: streamPxH,
        left: 0,
        width: '100%',
        height: chatAreaH,
        zIndex: 5 + (hotspot.order ?? 0),
        display: 'flex',
        flexDirection: 'column',
        background: CHAT_BG,
        // When keyboard is open, lift the whole chat panel up by the
        // keyboard height so the compose bar stays visible.
        transform: keyboardOffsetPx > 0 ? `translateY(-${keyboardOffsetPx}px)` : 'none',
        transition: 'transform 0.22s ease, height 0.22s ease',
        pointerEvents: 'all',
        // Prevent the chat from being scrolled along with the page
        touchAction: 'none',
      }}
    >
      <MessageList
        messages={chat.messages}
        loading={chat.loading}
        channelId={channelId}
        isAtBottom={chat.isAtBottom}
        scrollToBottom={chat.scrollToBottom}
        messagesRef={chat.messagesRef}
        onScroll={chat.handleScroll}
      />
      <ComposeBar
        channelId={channelId}
        composing={chat.composing}
        setComposing={chat.setComposing}
        onSend={chat.handleSend}
        inputRef={chat.inputRef}
      />
    </Box>
  );
}

// ── Public export ─────────────────────────────────────────────────────────────
export function EngLivestreamChat({ hotspot, participantName, participantId, headerHeight = 0, isMobileFullscreen = false, streamPxH = 0 }) {
  if (isMobileFullscreen) {
    return (
      <MobileFullscreenChat
        hotspot={hotspot}
        participantName={participantName}
        participantId={participantId}
        streamPxH={streamPxH}
      />
    );
  }

  return (
    <DesktopLivestreamChat
      hotspot={hotspot}
      participantName={participantName}
      participantId={participantId}
      headerHeight={headerHeight}
    />
  );
}